import { ApiClient } from '..';
import { getServiceUrl } from '../api.config';
import {
  BillingAddress,
  BillingCustomer,
  BillingPlan,
  BillingSubscription,
  ResponseOrError,
} from '../types';
import { ApiClientOptions } from '../types/api';
import qs from '../utils/query';

export default class BillingApi extends ApiClient {
  public constructor(options: ApiClientOptions = {}) {
    super(getServiceUrl('billing'), options);
  }

  public async subscribeToPlan({
    planId,
    couponId,
  }: {
    planId: string;
    couponId?: string;
  }) {
    try {
      const body: any = {};
      if (couponId) {
        body.coupon_id = couponId;
      }
      const response = await this.post<any, ResponseOrError>(
        `/billing/v1/plan/${planId}/subscribe`,
        body,
      );
      return { response };
    } catch (error) {
      return { error };
    }
  }

  public async cancelSubscription({
    subscriptionId,
  }: {
    subscriptionId: string;
  }): Promise<ResponseOrError> {
    try {
      const response = await this.delete(
        `/billing/v1/plan/subscription/${subscriptionId}`,
      );
      return { response };
    } catch (error) {
      return { error };
    }
  }

  public async fetchCustomer({
    withSubscriptions = false,
    useCache = false,
  }: {
    withSubscriptions?: boolean;
    useCache?: boolean;
  } = {}): Promise<ResponseOrError<BillingCustomer>> {
    try {
      const response = await this.get(
        '/billing/v2/customer/me',
        qs.stringify({ with_subscriptions: withSubscriptions ? 1 : 0 }, true),
        useCache,
      );
      return { response };
    } catch (error) {
      return { error };
    }
  }

  public async fetchCustomerSource({
    useCache = false,
  }: { useCache?: boolean } = {}): Promise<ResponseOrError> {
    try {
      const response = await this.get(
        '/billing/v1/customer/source',
        '',
        useCache,
      );
      return { response };
    } catch (error) {
      return { error };
    }
  }

  public async upsertCustomer({
    sourceId,
    sourceType,
    billingAddress,
    taxId,
  }: {
    sourceId: string;
    sourceType: string;
    billingAddress: BillingAddress;
    taxId?: string;
  }): Promise<ResponseOrError<undefined>> {
    try {
      await this.post('/billing/v1/customer/upsert', {
        source: {
          stripe_id: sourceId,
          type: sourceType,
        },
        billing_address: billingAddress,
        tax_id: taxId,
      });
      return {};
    } catch (error) {
      return { error };
    }
  }

  public async fetchComputedBill({
    planId,
    couponId,
  }: {
    planId: string;
    couponId?: string;
  }): Promise<ResponseOrError> {
    try {
      const body: any = {};
      if (couponId) {
        body.coupon_id = couponId;
      }
      const response = await this.post(
        `/billing/v1/plan/${planId}/bill/compute`,
        body,
      );
      return { response };
    } catch (error) {
      return { error };
    }
  }

  // TODO @alk-jerber add return type
  public async fetchNextBill({
    subscriptionId,
  }: {
    subscriptionId: string;
  }): Promise<ResponseOrError> {
    try {
      const response = await this.get(
        `/billing/v1/subscription/${subscriptionId}/bill/next`,
      );
      return { response };
    } catch (error) {
      return { error };
    }
  }

  public async fetchPlans({
    useCache = false,
  }: { useCache?: boolean } = {}): Promise<ResponseOrError<BillingPlan[]>> {
    try {
      const response = await this.get('/billing/v1/plans', '', useCache);
      return { response };
    } catch (error) {
      return { error };
    }
  }

  public async fetchPlan({
    stripePlanId,
    useCache = false,
  }: {
    stripePlanId: string;
    useCache?: boolean;
  }): Promise<ResponseOrError<BillingPlan>> {
    try {
      const response = await this.get(
        `/billing/v1/plans/${stripePlanId}`,
        '',
        useCache,
      );
      return { response };
    } catch (error) {
      return { error };
    }
  }

  public async fetchSubscriptions({
    useCache = false,
    organizationId,
  }: {
    useCache?: boolean;
    organizationId?: number;
  } = {}): Promise<ResponseOrError<BillingSubscription[]>> {
    try {
      const params: { organization_id?: number } = {};
      if (organizationId) {
        params.organization_id = organizationId;
      }
      const response = await this.get(
        '/billing/v1/plan/subscriptions',
        qs.stringify(params, true),
        useCache,
      );
      return { response };
    } catch (error) {
      return { error };
    }
  }

  public async confirmSubscription({
    subscriptionId,
    couponId,
  }: {
    subscriptionId: string;
    couponId?: string;
  }): Promise<ResponseOrError> {
    try {
      const body: any = {};
      if (couponId) {
        body.coupon_id = couponId;
      }
      const response = await this.post(
        `/billing/v1/plan/subscription/${subscriptionId}/confirm`,
        body,
      );
      return { response };
    } catch (error) {
      return { error };
    }
  }

  public async retryPayment({
    subscriptionId,
  }: {
    subscriptionId: string;
  }): Promise<ResponseOrError<BillingSubscription>> {
    try {
      const response = await this.post(
        `/billing/v1/plan/subscription/${subscriptionId}/retry_payment`,
        {},
      );
      return { response };
    } catch (error) {
      return { error };
    }
  }
}
